import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AsyncBoundary, Card, Notice } from '@kollez/design-system';
import { useAttempt } from '@/lib/queries';
import { formatIndianDate, formatTime } from '@kollez/shared';

/**
 * /assess/attempts/:attemptId — real attempt render for the 5g assessment
 * seed. AssessmentAttempt has sectionScores[], answers[] with aiScore +
 * reviewerScore + graded, integrityEvents[], and gates{} — surfaces them
 * as sections rather than an empty notice.
 *
 * Access: student can only open their own attempt; TPO/HoD/faculty/platform
 * can open any attempt in their college (getAttemptHandler enforces this).
 */

function severityTone(sev?: string): string {
  if (sev === 'critical' || sev === 'high') return 'badge-danger';
  if (sev === 'medium') return 'badge-warning';
  return '';
}

function statusTone(s: string): string {
  if (s === 'submitted' || s === 'graded') return 'badge-success';
  if (s === 'in_progress') return 'badge-info';
  if (s === 'flagged' || s === 'voided') return 'badge-danger';
  return '';
}

export function AssessAttemptPage() {
  const { attemptId = '' } = useParams();
  const attemptQ = useAttempt(attemptId);
  const attempt = attemptQ.data?.attempt;

  return (
    <AsyncBoundary
      kind="detail"
      loading={attemptQ.isLoading}
      error={attemptQ.error}
      empty={!attemptQ.isLoading && !attempt}
      emptyTitle="Attempt not found"
      emptyHint="The attempt id may be stale. Head to Results to open a specific attempt."
      onRetry={() => void attemptQ.refetch()}
    >
      {attempt && (
        <div className="stack">
          <Link className="btn btn-ghost justify-self-start" to="/assess/exams">
            <ArrowLeft size={14} />Back to exams
          </Link>

          <div className="page-header">
            <div className="ph-text">
              <p className="eyebrow">Attempt · {attempt._id.slice(-8)}</p>
              <h1 className="h-page">Assessment attempt</h1>
              <p className="subhead">
                <span className={`badge ${statusTone(attempt.status)}`}>{attempt.status}</span>
                {typeof attempt.totalScore === 'number' && (
                  <> · Total <b>{attempt.totalScore}</b></>
                )}
                {typeof attempt.pendingReview === 'number' && attempt.pendingReview > 0 && (
                  <> · <span className="text-warning">{attempt.pendingReview} pending review</span></>
                )}
              </p>
            </div>
          </div>

          <Card padding="md">
            <dl className="dl">
              <dt>Started</dt>
              <dd>{attempt.startedAt ? `${formatIndianDate(new Date(attempt.startedAt))} ${formatTime(attempt.startedAt)}` : '—'}</dd>
              <dt>Submitted</dt>
              <dd>{attempt.submittedAt ? `${formatIndianDate(new Date(attempt.submittedAt))} ${formatTime(attempt.submittedAt)}` : 'Not submitted'}</dd>
              <dt>Answers</dt>
              <dd>{attempt.answers?.length ?? 0} of {attempt.materializedQuestions?.length ?? 0}</dd>
            </dl>
          </Card>

          <Card padding="md">
            <h2 className="h-section">Section scores</h2>
            {!attempt.sectionScores || attempt.sectionScores.length === 0 ? (
              <p className="helper m-0">Scoring runs once the attempt is submitted.</p>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th className="r">Obtained</th>
                      <th className="r">Max</th>
                      <th>Gate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempt.sectionScores.map((s, i) => (
                      <tr key={i}>
                        <td className="cell-title">{s.section}</td>
                        <td className="r">{s.obtained ?? '—'}</td>
                        <td className="r cell-sub">{s.maxMarks ?? '—'}</td>
                        <td>
                          {typeof s.passed === 'boolean' ? (
                            <span className={`badge ${s.passed ? 'badge-success' : 'badge-danger'}`}>
                              {s.passed ? 'Pass' : 'Fail'}
                            </span>
                          ) : (
                            <span className="cell-sub">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {(attempt.gates?.faceMatch || attempt.gates?.photoIdCheck) && (
            <Card padding="md">
              <h2 className="h-section">Proctoring gates</h2>
              <dl className="dl">
                {attempt.gates?.faceMatch && (<>
                  <dt>Face match</dt>
                  <dd>
                    <span className={`badge ${attempt.gates.faceMatch.passed ? 'badge-success' : 'badge-warning'}`}>
                      {attempt.gates.faceMatch.passed ? 'Passed' : 'Flagged'}
                    </span>
                    {attempt.gates.faceMatch.note && <> · <span className="cell-sub">{attempt.gates.faceMatch.note}</span></>}
                  </dd>
                </>)}
                {attempt.gates?.photoIdCheck && (<>
                  <dt>Photo-ID check</dt>
                  <dd>
                    <span className={`badge ${attempt.gates.photoIdCheck.passed ? 'badge-success' : 'badge-warning'}`}>
                      {attempt.gates.photoIdCheck.passed ? 'Passed' : 'Flagged'}
                    </span>
                    {attempt.gates.photoIdCheck.note && <> · <span className="cell-sub">{attempt.gates.photoIdCheck.note}</span></>}
                  </dd>
                </>)}
              </dl>
            </Card>
          )}

          <Card padding="md">
            <h2 className="h-section">Integrity timeline</h2>
            {!attempt.integrityEvents || attempt.integrityEvents.length === 0 ? (
              <p className="helper m-0">No integrity flags on this attempt.</p>
            ) : (
              <ol className="timeline">
                {attempt.integrityEvents.map((ev, i) => (
                  <li key={i} data-state="done">
                    <span className="t-dot" />
                    <div className="t-title">
                      {ev.kind ?? 'event'}
                      {ev.severity && <> · <span className={`badge ${severityTone(ev.severity)}`}>{ev.severity}</span></>}
                    </div>
                    <div className="t-meta">
                      {ev.at ? `${formatIndianDate(new Date(ev.at))} ${formatTime(ev.at)}` : ''}
                      {ev.note && <> · <span className="cell-sub">{ev.note}</span></>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          <Card padding="md">
            <h2 className="h-section">Answers</h2>
            {!attempt.answers || attempt.answers.length === 0 ? (
              <p className="helper m-0">No answers recorded.</p>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Section</th>
                      <th className="r">AI</th>
                      <th className="r">Reviewer</th>
                      <th className="r">Final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempt.answers.map((a, i) => (
                      <tr key={i}>
                        <td className="cell-title cell-sub">{a.question.slice(-8)}</td>
                        <td className="cell-sub">{a.section ?? '—'}</td>
                        <td className="r cell-sub">
                          {typeof a.aiScore === 'number'
                            ? `${a.aiScore}${typeof a.aiConfidence === 'number' ? ` · ${Math.round(a.aiConfidence * 100)}%` : ''}`
                            : '—'}
                        </td>
                        <td className="r">
                          {typeof a.reviewerScore === 'number' ? (
                            <span className="badge">{a.reviewerScore}</span>
                          ) : a.graded ? '—' : <span className="cell-sub text-warning">pending</span>}
                        </td>
                        <td className="r"><b>{typeof a.score === 'number' ? a.score : '—'}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {attempt.status === 'in_progress' && (
            <Notice tone="info">This attempt is still in progress. Scores render once the student submits.</Notice>
          )}
        </div>
      )}
    </AsyncBoundary>
  );
}
